import { useEffect, useState } from 'react';
import { Tr, Td, Icon, Flex, Heading, Stack } from '@chakra-ui/react';
import { FiEye, FiEdit } from 'react-icons/fi';
import { MdDeleteOutline, MdOutlineSupervisorAccount } from 'react-icons/md';
import { PatientsProps } from '@/features/registers/types';
import {
  TopSection,
  Tables,
  GenericModal,
  EditPatientForm,
  ViewPatientInfo,
  usePatientMutation,
  usePatientEdit,
} from '@/features/registers';
import { api } from '@/lib';
import { useUserStore, useProfessionalStore, useUnidadeStore } from '@/stores';
import { useUserTermQuery } from '@/hooks';
import { PaginationButton } from '../components/Pagination/PaginationButton';
import { DeleteUserModal } from '../components/DeleteModals/DeleteUserModal';
import { AssociatePatientForm } from '../components/AssociatePatientForm';

export function PatientsListPage() {
  const [patient, setPatient] = useState<any>();
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);
  const [isAssociateOpen, setIsAssociateOpen] = useState<boolean>(false);
  // const [pageNumber, setPageNumber] = useState(0);
  const [dataPatients, setDataPatients] = useState<any>();
  const [text, setText] = useState('');
  const [search, setSearch] = useState<any>([]);
  const [head, setHead] = useState<string[]>([]);
  const user = useUserStore(state => state.user);
  const { data: userTerm } = useUserTermQuery();
  // const { data } = usePatientListQuery(pageNumber);
  const { mutate: patientMutate, isLoading: isPatientMutationLoading } =
    usePatientMutation();
  const [selectPage, setSelectPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const { mutate: patientEdit } = usePatientEdit();

  const { professional } = useProfessionalStore();
  const unidadeId = useUnidadeStore(state => state.unidade?.id);

  const onCloseModal = () => setIsEditOpen(false);
  const onCloseDeleteModal = () => setIsDeleteOpen(false);
  const onCloseViewModal = () => setIsViewOpen(false);
  const onCloseAssociateModal = () => setIsAssociateOpen(false);

  const onOpenEditModal = (p: any) => {
    setPatient(p);
    setIsEditOpen(true);
  };

  const onDeleteEditModal = (p: any) => {
    setPatient(p);
    setIsDeleteOpen(true);
  };

  const onOpenViewModal = (p: any) => {
    setPatient(p);
    setIsViewOpen(true);
  };

  const onOpenAssociateModal = (p: any) => {
    setPatient(p);
    setIsAssociateOpen(true);
  };

  const setDataContent = async (back = false, next = false) => {
    const params = {
      usuarioId: user?.type === 'medico' ? user?.id : professional?.idMedico,
      idUnidade: unidadeId,
      pageSize: 5,
      pageNumber:
        !back && !next
          ? 1
          : back && selectPage > 1
          ? selectPage - 1
          : next
          ? selectPage + 1
          : selectPage,
    };

    const { data: total } = await api.get(
      `/paciente/obter-paciente-profissional-paginado`,
      {
        params: {
          usuarioId: params.usuarioId,
          idUnidade: params.idUnidade,
        },
      },
    );
    setTotalPage(Math.ceil(total.data.length / 5)); // mock solicitar total page no endpoint

    // const response = await api.get(
    //   '/paciente/obter-paciente-gestormaster-paginado',
    //   { params },
    // );

    await api
      .get('/paciente/obter-paciente-profissional-paginado', { params })
      .then(response => {
        if (response.data.data.length > 0) {
          setDataPatients(response.data.data);
          setSearch(response.data.data);
          if (back) {
            setSelectPage(selectPage === 1 ? 1 : selectPage - 1);
          }
          if (next) {
            setSelectPage(selectPage + 1);
          }
        } else if (!back && !next) {
          setDataPatients([]);
          setSearch([]);
        }
      })
      .catch(e => {
        setDataPatients([]);
        setSearch([]);
      });
  };

  useEffect(() => {
    setSelectPage(1);
    setDataContent(false, false);
  }, [professional, unidadeId]);

  useEffect(() => {
    setDataContent();
  }, [isEditOpen, isDeleteOpen]);

  useEffect(() => {
    if (dataPatients) {
      const lowerSearch = text.toLowerCase();
      const result = dataPatients.filter((d: any) => {
        return d.nome.toLowerCase().includes(lowerSearch);
      });

      setSearch(result);
    }
  }, [text]);

  useEffect(() => {
    if (
      user?.type === 'secretario' ||
      user?.type === 'assistente' ||
      user?.type === 'medico' ||
      user?.type !== 'paciente'
    ) {
      head.push('EDITAR');
    }
    if (
      user?.type !== 'secretario' &&
      user?.type !== 'paciente' &&
      user?.type !== 'medico' &&
      user?.type !== 'assistente'
    ) {
      head.push('DELETAR');
    }
    if (user?.type !== 'paciente') {
      head.push('ASSOCIAR');
    }
  }, []);

  const showEditBtn = (patientData: PatientsProps) => {
    if (
      user?.type === 'secretario' ||
      user?.type === 'assistente' ||
      user?.type === 'medico' ||
      user?.type !== 'paciente'
    ) {
      return (
        <Icon
          as={FiEdit}
          onClick={() => {
            if (patientData.primeiroAcesso) return;
            onOpenEditModal(patientData);
          }}
          cursor="pointer"
          color={
            patientData.primeiroAcesso ? 'brand-dark.100' : 'brand-dark.200'
          }
          fontSize="20px"
          fontWeight="400"
          marginRight="15"
        />
      );
    }

    return null;
  };

  const showDeleteBtn = (patientData: PatientsProps) => {
    if (
      user?.type !== 'secretario' &&
      user?.type !== 'paciente' &&
      user?.type !== 'medico' &&
      user?.type !== 'assistente'
    ) {
      return (
        <Icon
          as={MdDeleteOutline}
          onClick={() => {
            if (patientData.primeiroAcesso && user?.type !== 'gestor') return;
            onDeleteEditModal(patientData);
          }}
          cursor="pointer"
          color={
            patientData.primeiroAcesso && user?.type !== 'gestor'
              ? 'brand-dark.100'
              : 'brand-dark.200'
          }
          fontSize="20px"
          fontWeight="400"
          marginRight="15"
        />
      );
    }

    return null;
  };

  const showAssociateBtn = (patientData: PatientsProps) => {
    if (user?.type !== 'paciente') {
      return (
        <Icon
          as={MdOutlineSupervisorAccount}
          onClick={() => {
            if (patientData.primeiroAcesso) return;
            onOpenAssociateModal(patientData);
          }}
          cursor="pointer"
          color={
            patientData.primeiroAcesso ? 'brand-dark.100' : 'brand-dark.200'
          }
          fontSize="20px"
          fontWeight="400"
          marginRight="15"
        />
      );
    }

    return null;
  };

  return (
    <>
      <TopSection
        title="Pacientes Cadastrados"
        search={text}
        onChange={(str: any) => setText(str)}
      />

      <Tables
        head={[
          'Nome do Paciente',
          'Celular',
          'Email',
          'Status',
          'Visualizar',
          ...head,
        ]}
      >
        {search?.map((patientData: PatientsProps) => {
          return (
            <Tr key={patientData.id}>
              <Td>{patientData.nome}</Td>
              <Td>{patientData.celular}</Td>
              <Td>{patientData.email}</Td>
              <Td style={{ color: patientData.primeiroAcesso && 'red' }}>
                {patientData.primeiroAcesso ? 'PENDENTE' : 'VALIDADO'}
              </Td>
              <Td>
                <Icon
                  as={FiEye}
                  onClick={() => onOpenViewModal(patientData)}
                  cursor="pointer"
                  color="brand-dark.200"
                  fontSize="20px"
                  fontWeight="400"
                  marginRight="15"
                />
              </Td>
              <Td>{showEditBtn(patientData)}</Td>
              {user?.type !== 'secretario' &&
                user?.type !== 'paciente' &&
                user?.type !== 'medico' &&
                user?.type !== 'assistente' && (
                  <Td>{showDeleteBtn(patientData)}</Td>
                )}
              <Td>{showAssociateBtn(patientData)}</Td>
            </Tr>
          );
        })}
      </Tables>
      {/* <Pagination
        page={pageNumber}
        totalPage={dataPatients?.totalPages || 0}
        totalRegisters={dataPatients?.data.length}
      /> */}
      <Flex
        direction={['column', 'column', 'row']}
        w="100%"
        align="center"
        justify="space-between"
      >
        <Heading
          size="xs"
          w="100%"
          mb={['4', '4', '0']}
          color="#707070"
          fontWeight="medium"
        >
          {/* Página {selectPage} de {dataPatients?.data.length} registros */}
          Página {selectPage}/{totalPage}
        </Heading>
        <Stack
          direction={['column', 'row']}
          spacing="4"
          w="100%"
          justify="flex-end"
        >
          <PaginationButton
            text="Voltar"
            onClick={() => setDataContent(true, false)}
          />
          {/* <PaginationButton text="Ultimo" onClick={() => setSelectPage(1)} /> */}
          {/* <NumbersPage
            onClick={value => setSelectPage(value)}
            page={selectPage}
            totalPage={dataPatients?.totalPages || 0}
          /> */}
          <PaginationButton
            text="Proximo"
            onClick={() => setDataContent(false, true)}
          />
          {/* <PaginationButton
            text="Ultimo"
            onClick={() => setSelectPage(dataPatients?.totalPages || 0)}
          /> */}
        </Stack>
      </Flex>
      <GenericModal
        open={isEditOpen}
        textAlign="center"
        title="Editar Paciente"
        onClose={onCloseModal}
        closeOnOverlayClick={false}
      >
        <EditPatientForm patient={patient} onCancel={onCloseModal} />
        {/* <EditUserPatientForm patient={patient} onCancel={onCloseViewModal} /> */}
      </GenericModal>
      <GenericModal
        open={isViewOpen}
        title={`Paciente: ${patient && patient.nome}`}
        onClose={onCloseViewModal}
      >
        <ViewPatientInfo patient={patient} />
      </GenericModal>
      <GenericModal
        open={isDeleteOpen}
        textAlign="center"
        title={`Deletar Paciente: ${patient && patient.nome}`}
        onClose={onCloseDeleteModal}
      >
        <DeleteUserModal
          user={patient}
          type="patient"
          onCancel={onCloseDeleteModal}
        />
      </GenericModal>
      <GenericModal
        open={isAssociateOpen}
        textAlign="center"
        title="Vincular com Profissional"
        onClose={onCloseAssociateModal}
        closeOnOverlayClick={false}
      >
        <AssociatePatientForm
          patient={patient}
          onCancel={onCloseAssociateModal}
        />
      </GenericModal>
    </>
  );
}
